import { useCallback, useEffect,useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppHeader } from '@features/admin-v2';
import {
  ArrowBack,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Error as ErrorIcon,
  FileDownload as ImportIcon,
  MergeType as MergeIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import type { MatchedPatientData } from '@store/generated/admin';
import { useSnackbar } from 'notistack';

import { PatientMatchReview } from '../components/patient-match-review';
import {
  useGetImportSessionStatusQuery,
  useGetMatchedPatientsQuery,
  useGetV2AdminOrganizationsByOrganizationIdQuery,
  useImportOperationTracking,
  useImportSelectedPatientsMutation,
  useImportUnmatchedPatientsMutation,
  useMergeMultiplePatientsMutation,
  useRejectPatientsMutation,
} from '../store/hooks';
import {
  formatPatientStats,
  getPollingInterval,
  getProgressPhaseDescription,
  getStatusColor,
  getStatusLabel,
  isSessionProcessing,
} from '../utils/import-status-helpers';

interface ImportedPatientsListProps {
  sessionId: string;
  organizationId?: string;
  pollingInterval?: number | false;
}

function ImportedPatientsList({ sessionId, organizationId, pollingInterval }: ImportedPatientsListProps) {
  const navigate = useNavigate();
  const [selectedPatients, setSelectedPatients] = useState<Set<string>>(new Set());
  const [tabValue, setTabValue] = useState(0);
  const [activeOperations, setActiveOperations] = useState<Set<string>>(new Set());
  const { enqueueSnackbar } = useSnackbar();

  // Get matched patients data with proper UUIDs
  const { data: matchedData, isLoading: isLoadingPatients, error: patientsError, refetch: refetchPatients } = useGetMatchedPatientsQuery(
    {
      sessionId,
      limit: 10000  // Fetch up to 10,000 patients to avoid pagination limits
    },
    {
      // Add polling to sync with session status updates
      pollingInterval: pollingInterval === false ? undefined : pollingInterval
    }
  );

  const [importSelectedPatients, { isLoading: isImporting }] = useImportSelectedPatientsMutation();
  const [importUnmatchedPatients, { isLoading: isImportingUnmatched }] = useImportUnmatchedPatientsMutation();
  const [rejectPatients, { isLoading: isRejecting }] = useRejectPatientsMutation();
  const [mergeMultiplePatients, { isLoading: isMerging }] = useMergeMultiplePatientsMutation();

  // Operation tracking for async imports
  const operationTracking = useImportOperationTracking({
    sessionId,
    onOperationComplete: (operation) => {
      setActiveOperations(prev => {
        const next = new Set(prev);
        next.delete(operation.operationId);
        return next;
      });
      enqueueSnackbar(
        `Import operation completed successfully! Imported ${operation.progress?.imported_count || 0} patients.`,
        { variant: 'success' }
      );
      // Refresh data after completion
      refetchPatients();
    },
    onOperationError: (operation) => {
      setActiveOperations(prev => {
        const next = new Set(prev);
        next.delete(operation.operationId);
        return next;
      });
      enqueueSnackbar(
        `Import operation failed: ${operation.error || 'Unknown error'}`,
        { variant: 'error' }
      );
    },
  });

  // Smart data refresh: periodically refresh data when operations are active
  useEffect(() => {
    if (activeOperations.size === 0) return;

    const refreshInterval = setInterval(() => {
      refetchPatients();
    }, 10000); // Refresh every 10 seconds while operations are active

    return () => clearInterval(refreshInterval);
  }, [activeOperations.size, refetchPatients]);

  const handleImportSelected = async () => {
    if (selectedPatients.size === 0) {
      return;
    }

    const patientIds = Array.from(selectedPatients);

    const requestData = {
      sessionId,
      extractedPatientsRequest: {
        patient_ids: patientIds,
        skip_duplicates: false,
        update_existing: true,
      }
    };

    try {
      const result = await importSelectedPatients(requestData).unwrap();

      // Check if the operation is async
      if (result.is_async && result.operation_id) {
        enqueueSnackbar(
          `Import started for ${selectedPatients.size} patient${selectedPatients.size > 1 ? 's' : ''}. Processing in background...`,
          { variant: 'info' }
        );

        // Add to active operations and start tracking
        setActiveOperations(prev => new Set(prev).add(result.operation_id));
        operationTracking.startTracking(result.operation_id);
      } else {
        // Synchronous completion
        enqueueSnackbar(
          `Successfully imported ${result.imported_count || selectedPatients.size} patient${selectedPatients.size > 1 ? 's' : ''} to main database`,
          { variant: 'success' }
        );
        refetchPatients();
      }

      // Clear selections regardless of sync/async
      setSelectedPatients(new Set());
    } catch (error: any) {
      console.error('Import error:', error);
      enqueueSnackbar(
        error?.data?.message || `Failed to import patients. Please try again.`,
        { variant: 'error' }
      );
    }
  };

  const handleImportAllUnmatched = async () => {
    try {
      const result = await importUnmatchedPatients({
        sessionId,
        importUnmatchedRequest: {} // Empty object per API spec
      }).unwrap();

      // Check if the operation is async
      if (result.is_async && result.operation_id) {
        enqueueSnackbar(
          'Import of all unmatched patients started. Processing in background...',
          { variant: 'info' }
        );

        // Add to active operations and start tracking
        setActiveOperations(prev => new Set(prev).add(result.operation_id));
        operationTracking.startTracking(result.operation_id);
      } else {
        // Synchronous completion
        enqueueSnackbar(
          `Successfully imported ${result.imported_count || 0} new patient${(result.imported_count || 0) !== 1 ? 's' : ''} to main database`,
          { variant: 'success' }
        );
        refetchPatients();
      }
    } catch (error: any) {
      console.error('Import unmatched error:', error);
      enqueueSnackbar(
        error?.data?.message || `Failed to import unmatched patients. Please try again.`,
        { variant: 'error' }
      );
    }
  };

  const handleRejectSelected = async () => {
    if (selectedPatients.size === 0) return;

    try {
      await rejectPatients({
        sessionId,
        rejectPatientsRequest: {
          patient_ids: Array.from(selectedPatients),
          reason: 'Rejected by admin during review',
        }
      }).unwrap();

      enqueueSnackbar(
        `Successfully removed ${selectedPatients.size} patient${selectedPatients.size > 1 ? 's' : ''}`,
        { variant: 'success' }
      );

      // Clear selections and refresh data
      setSelectedPatients(new Set());
      refetchPatients();
    } catch (error: any) {
      console.error('Reject error:', error);
      enqueueSnackbar(
        error?.data?.message || `Failed to remove patients. Please try again.`,
        { variant: 'error' }
      );
    }
  };

  // Helper function to check if a patient is already processed
  const isPatientProcessed = (patient: MatchedPatientData): boolean => {
    const processedStatuses = ['imported', 'merged', 'completed', 'failed'];
    return processedStatuses.includes(patient.import_status?.toLowerCase());
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && matchedData?.patients) {
      const selectablePatients = matchedData.patients.filter((p: MatchedPatientData) => !isPatientProcessed(p));

      const allIds = new Set(
        selectablePatients
          .map((p: MatchedPatientData) => p.extracted_patient_id)
          .filter(Boolean)
      );

      setSelectedPatients(allIds);
    } else {
      setSelectedPatients(new Set());
    }
  };

  const handleSelectPatient = (patientId: string) => {
    const newSelected = new Set(selectedPatients);
    if (newSelected.has(patientId)) {
      newSelected.delete(patientId);
    } else {
      newSelected.add(patientId);
    }
    setSelectedPatients(newSelected);
  };

  const handleMergeDecision = async (
    patientId: string,
    decision: 'merge' | 'import_as_new',
    mergeTargetId?: string,
    allSourceIds?: string[] // Now contains both extracted and existing patient IDs
  ) => {
    if (decision === 'import_as_new') {
      // Import as new patient
      try {
        await importSelectedPatients({
          sessionId,
          extractedPatientsRequest: {
            patient_ids: [patientId]
          },
        }).unwrap();
        enqueueSnackbar(
          `Successfully imported 1 patient`,
          { variant: 'success' }
        );
        // Clear selection after successful import
        setSelectedPatients(new Set());
        // Navigate back to the data migrations list
        setTimeout(() => navigate('/admin/data-migrations'), 500);
      } catch (error: any) {
        console.error('Import error:', error);
        enqueueSnackbar(
          error?.data?.message || `Failed to import patient. Please try again.`,
          { variant: 'error' }
        );
      }
    } else if (decision === 'merge' && mergeTargetId && allSourceIds) {
      // Merge patients - separate extracted vs existing patient IDs
      try {
        // Use the organizationId from props
        const orgId = organizationId || '';

        // Separate extracted patient IDs from existing patient IDs
        const sourceExtractedIds = allSourceIds.filter(id => id === patientId);
        const sourceExistingIds = allSourceIds.filter(id => id !== patientId);

        await mergeMultiplePatients({
          mergeMultiplePatientsRequest: {
            organization_id: orgId,
            target_patient_id: mergeTargetId,
            source_existing_patient_ids: sourceExistingIds,
            source_extracted_patient_ids: sourceExtractedIds
          },
        }).unwrap();

        const totalSourceCount = sourceExtractedIds.length + sourceExistingIds.length;
        enqueueSnackbar(
          `Successfully merged ${totalSourceCount} patient${totalSourceCount !== 1 ? 's' : ''} into target patient`,
          { variant: 'success' }
        );
        // Clear selection after successful merge
        setSelectedPatients(new Set());
        // Navigate back to the data migrations list
        setTimeout(() => navigate('/admin/data-migrations'), 500);
      } catch (error: any) {
        console.error('Merge error:', error);
        enqueueSnackbar(
          error?.data?.message || `Failed to merge patients. Please try again.`,
          { variant: 'error' }
        );
      }
    }
  };

  // Separate patients by status using enhanced logic
  const categorizedPatients = useMemo(() => {
    if (!matchedData?.patients) {
      return { new: [], matched: [], errors: [] };
    }

    const categorized = {
      new: [] as MatchedPatientData[],
      matched: [] as MatchedPatientData[],
      errors: [] as MatchedPatientData[]
    };

    matchedData.patients.forEach((p: MatchedPatientData) => {
      // Check multiple ways a patient could have matches
      const hasNewMatches = p.potential_matches && p.potential_matches.length > 0;
      const hasLegacyMatch = p.matched_patient_id && p.matched_patient_data;
      const hasMatchStatus = p.import_status === 'matched';
      const isError = p.import_status === 'failed';

      if (isError) {
        categorized.errors.push(p);
      } else if (hasNewMatches || hasLegacyMatch || hasMatchStatus) {
        categorized.matched.push(p);
      } else {
        categorized.new.push(p);
      }
    });

    return categorized;
  }, [matchedData]);


  if (isLoadingPatients) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading patient data...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (patientsError) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            Failed to load patient data. Please try again.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!matchedData?.patients || matchedData.patients.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Patient Data Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No patient records found for this import session.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h6">
              Extracted Patient Records ({matchedData?.total_imported || 0})
            </Typography>
            {selectedPatients.size > 0 && (
              <Typography variant="body2" color="primary">
                {selectedPatients.size} selected
              </Typography>
            )}
          </Box>
          <Box display="flex" gap={1}>
            {/* Import All Unmatched button - show when there are NEW patients */}
            {categorizedPatients.new.length > 0 && (
              <Button
                variant="contained"
                color="success"
                onClick={handleImportAllUnmatched}
                disabled={isImporting || isImportingUnmatched || isRejecting || isMerging}
                startIcon={isImportingUnmatched ? <CircularProgress size={16} /> : <ImportIcon />}
              >
                {isImportingUnmatched ? 'Importing...' : `Import All New Patients (${categorizedPatients.new.length})`}
              </Button>
            )}

            {/* Selected actions - show when patients are selected */}
            {selectedPatients.size > 0 && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleImportSelected}
                  disabled={isImporting || isImportingUnmatched || isRejecting || isMerging}
                  startIcon={isImporting ? <CircularProgress size={16} /> : <ImportIcon />}
                >
                  {isImporting ? 'Importing...' : `Import Selected (${selectedPatients.size})`}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleRejectSelected}
                  disabled={isImporting || isImportingUnmatched || isRejecting || isMerging}
                  startIcon={isRejecting ? <CircularProgress size={16} /> : <DeleteIcon />}
                >
                  {isRejecting ? 'Removing...' : 'Remove Selected'}
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                indeterminate={
                  selectedPatients.size > 0 &&
                  selectedPatients.size < categorizedPatients.new.length + categorizedPatients.matched.length
                }
                checked={
                  selectedPatients.size > 0 &&
                  selectedPatients.size === categorizedPatients.new.length + categorizedPatients.matched.length
                }
                onChange={handleSelectAll}
              />
            }
            label="Select All"
          />
        </Box>

        {/* Display active operations progress */}
        {activeOperations.size > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={20} />
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Processing Import Operations ({activeOperations.size})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Import operations are running in the background. The page will refresh automatically when complete.
                </Typography>
              </Box>
            </Box>
          </Alert>
        )}

        <Tabs value={tabValue} onChange={(_e, v: number) => setTabValue(v)} sx={{ mb: 2 }}>
          <Tab
            label={`New Patients (${categorizedPatients.new.length})`}
            icon={<CheckCircleIcon />}
            iconPosition="start"
          />
          <Tab
            label={`Potential Matches (${categorizedPatients.matched.length})`}
            icon={<MergeIcon />}
            iconPosition="start"
          />
          {categorizedPatients.errors.length > 0 && (
            <Tab
              label={`Errors (${categorizedPatients.errors.length})`}
              icon={<ErrorIcon />}
              iconPosition="start"
            />
          )}
        </Tabs>

        <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
          {(tabValue === 0 ? categorizedPatients.new :
            tabValue === 1 ? categorizedPatients.matched :
            categorizedPatients.errors).map((matchedPatient: MatchedPatientData, index: number) => {
            const isProcessed = isPatientProcessed(matchedPatient);
            const isSelectable = !isProcessed;
            const patientIdToUse = matchedPatient.extracted_patient_id;
            const isSelected = patientIdToUse ? selectedPatients.has(patientIdToUse) : false;

            return (
              <PatientMatchReview
                key={patientIdToUse || index}
                patient={matchedPatient}
                isSelected={isSelected}
                isProcessed={isProcessed}
                onToggleSelection={isSelectable ? handleSelectPatient : undefined}
                onMergeDecision={handleMergeDecision}
                isLoading={isImporting || isImportingUnmatched || isRejecting || isMerging}
              />
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}

// Helper function to get status icon
const getStatusIcon = (status: string) => {
  const statusLower = status?.toLowerCase();

  switch (statusLower) {
    case 'completed':
      return <CheckCircleIcon fontSize="small" />;
    case 'processing':
    case 'in_progress':
      return <CircularProgress size={16} />;
    case 'failed':
    case 'error':
      return <ErrorIcon fontSize="small" />;
    case 'pending':
      return <ScheduleIcon fontSize="small" />;
    default:
      return undefined;
  }
};

export default function ImportSessionDetailPage() {
  const navigate = useNavigate();
  const { organizationId, sessionId } = useParams();

  // Fetch organization details
  const { data: orgData } = useGetV2AdminOrganizationsByOrganizationIdQuery(
    { organizationId: organizationId || '' },
    { skip: !organizationId }
  );

  const [pollingInterval, setPollingInterval] = useState<number | false>(false);

  const { data: session, isLoading, error, refetch } = useGetImportSessionStatusQuery(
    { sessionId: sessionId! },
    {
      skip: !sessionId,
      pollingInterval: pollingInterval === false ? undefined : pollingInterval,
    }
  );

  // Get matched patients data for accurate statistics
  const { data: matchedData } = useGetMatchedPatientsQuery(
    {
      sessionId: sessionId!,
      limit: 10000  // Fetch up to 10,000 patients to avoid pagination limits
    },
    {
      skip: !sessionId,
      pollingInterval: pollingInterval === false ? undefined : pollingInterval,
    }
  );

  // Update polling interval based on session status
  useEffect(() => {
    if (session?.status) {
      const interval = getPollingInterval(session.status);
      setPollingInterval(interval);
    }
  }, [session?.status]);

  const handleBack = useCallback(() => {
    navigate(`/admin-v2/organization/${organizationId}/data-migrations`);
  }, [navigate, organizationId]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (!organizationId || !sessionId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert severity="error">Invalid session parameters</Alert>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Box mb={3}>
          <Button
            onClick={handleBack}
            startIcon={<ArrowBack />}
            className="text-gray-600 hover:text-gray-900"
          >
            Back to Data Migrations
          </Button>
        </Box>

        <Box mb={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box>
              <Typography variant="h4" component="h1">
                Data Review Session
              </Typography>
              {matchedData && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {formatPatientStats(
                    matchedData.total_imported || 0,
                    matchedData.patients?.length || 0,
                    matchedData.patients?.filter(p => p.import_status === 'failed').length || 0,
                    matchedData.patients?.filter(p => !p.potential_matches || p.potential_matches.length === 0).length || 0
                  )}
                </Typography>
              )}
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              {/* Action buttons for completed sessions */}
              {session?.status?.toLowerCase() === 'completed' && (
                <>
                  <Tooltip title="Import all patients into the system - Coming soon">
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled
                        startIcon={<ImportIcon />}
                      >
                        Import All
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Review and merge duplicate patients - Coming soon">
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled
                        startIcon={<MergeIcon />}
                      >
                        Merge Duplicates
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Discard this session - Coming soon">
                    <span>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        disabled
                        startIcon={<DeleteIcon />}
                      >
                        Discard
                      </Button>
                    </span>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                </>
              )}
              <IconButton onClick={() => refetch()} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load session details. Please try again.
          </Alert>
        )}

        {session && (
          <>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Organization
                    </Typography>

                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {orgData?.name || 'Loading...'}
                      </Typography>
                    </Box>

                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Session Status
                      </Typography>
                      <Chip
                        label={getStatusLabel(session.status, session.progress_percentage)}
                        color={getStatusColor(session.status)}
                        icon={getStatusIcon(session.status)}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>

                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Uploaded
                      </Typography>
                      <Typography variant="body1">
                        {new Date(session.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Extraction Statistics
                    </Typography>

                    {isSessionProcessing(session.status) && (
                      <Box mb={3}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body2" color="text.secondary">
                            {getProgressPhaseDescription(session.status, session.progress_percentage, session.processed_patients, session.total_patients)}
                          </Typography>
                          <Typography variant="body2" fontWeight="medium" color="primary">
                            {session.progress_percentage || 0}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={session.progress_percentage || 0}
                          sx={{ height: 10, borderRadius: 5 }}
                        />
                        {matchedData && matchedData.total_imported > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {matchedData.patients?.length || 0} of {matchedData.total_imported} patients loaded
                          </Typography>
                        )}
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Primary stats in a row */}
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon color="success" />
                            <Box>
                              <Typography variant="h5" color="success.main">
                                {matchedData?.patients?.filter(p => !p.potential_matches || p.potential_matches.length === 0).length || 0}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                New Patients
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Box sx={{ flex: 1, p: 2, bgcolor: 'warning.lighter', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MergeIcon color="warning" />
                            <Box>
                              <Typography variant="h5" color="warning.main">
                                {matchedData?.patients?.filter(p => p.potential_matches && p.potential_matches.length > 0).length || 0}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Duplicates
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>

                      {/* Secondary stats */}
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                          <Typography variant="h6">
                            {matchedData?.total_imported || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Total Extracted
                          </Typography>
                        </Box>

                        {matchedData?.patients && matchedData.patients.filter(p => p.import_status === 'failed').length > 0 && (
                          <Box sx={{ flex: 1, p: 1.5, bgcolor: 'error.lighter', borderRadius: 1 }}>
                            <Typography variant="h6" color="error.main">
                              {matchedData.patients.filter(p => p.import_status === 'failed').length}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Parse Errors
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Grid>

                </Grid>

                {session.status?.toLowerCase() === 'cancelled' && (
                  <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Session Cancelled:</strong> This extraction session was cancelled and will not process any patient data.
                      </Typography>
                    </Alert>
                  </Box>
                )}

                {session.error_messages && session.error_messages.length > 0 && (
                  <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" gutterBottom color="error">
                      Error Messages
                    </Typography>
                    {session.error_messages.map((error: any, index: number) => (
                      <Alert key={index} severity="error" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          <strong>{error.error_type || 'Error'}:</strong> {error.message}
                        </Typography>
                        {error.timestamp && (
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(error.timestamp)}
                          </Typography>
                        )}
                      </Alert>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>

            <ImportedPatientsList sessionId={sessionId} organizationId={organizationId} pollingInterval={pollingInterval} />
          </>
        )}
      </main>
    </div>
  );
}